<template>
  <div class="flex flex-col gap-4 w-full">
    <!-- Hero + pulse -->
    <div class="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <!-- Hero: what came into the business + revenue mix -->
      <div
        class="lg:col-span-3 rounded-[20px] px-4 pt-4 pb-4 text-white relative overflow-hidden"
        style="background: linear-gradient(140deg,#2c1461 0%,#49238f 60%,#6831d0 100%); box-shadow: 0 6px 20px rgba(73,35,143,.18)">
        <div class="flex items-baseline justify-between">
          <span class="text-xs opacity-75 font-medium flex items-center gap-2">
            {{ selectedMix ? selectedMix.label : 'נכנס לעסק' }} · {{ periodLabel }}
            <span
              v-if="data.revenue.estimated"
              class="text-[9.5px] font-bold bg-white/20 rounded-full px-2 py-0.5 leading-none">
              הערכה לפי מחירון
            </span>
          </span>
          <span v-if="selectedMix" class="text-[11px] font-bold opacity-85">
            {{ Math.round((selectedMix.value / (data.revenue.netRevenue || 1)) * 100) }}% מסך ההכנסה
          </span>
          <span v-else-if="revenueDelta" class="text-[11px] font-bold" :class="revenueDelta.up ? 'text-emerald-300' : 'text-red-300'">
            {{ revenueDelta.text }}
          </span>
        </div>
        <div class="text-[34px] font-bold leading-tight mt-0.5 mb-3.5" style="letter-spacing: -0.5px">
          {{ formatCurrency(selectedMix ? selectedMix.value : data.revenue.netRevenue) }}
        </div>
        <RevenueMixBar v-if="hasMixRevenue" :segments="mixSegments" @pick="seg => selectedMix = seg"/>
        <div v-else class="text-xs opacity-70">אין תשלומים בתקופה שנבחרה</div>
      </div>

      <!-- Pulse — the period's facts as sentences -->
      <div class="lg:col-span-2 bg-white border border-gray-200 rounded-[18px] px-4 py-1.5 flex flex-col justify-center">
        <div
          v-for="(row, i) of pulseRows"
          :key="i"
          class="flex items-center gap-3 py-2.5"
          :class="{'border-b border-gray-100': i < pulseRows.length - 1}">
          <div class="w-[30px] h-[30px] rounded-[9px] shrink-0 flex items-center justify-center" :style="{background: row.tint}">
            <i :class="`pi ${row.icon}`" class="text-sm" :style="{color: row.fg}"/>
          </div>
          <div class="text-[13px] text-gray-700 leading-snug" v-html="row.html"/>
        </div>
      </div>
    </div>

    <!-- AI digest + inline Q&A — always covers the last 30 days, independent of the date filter -->
    <AnalyticsAiBlock
      @ask="q => emit('ask', q)"
      @questions="qs => emit('questions', qs)"/>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <!-- When the money came in -->
      <section class="flex flex-col">
        <h3 class="section-title">מתי נכנס הכסף</h3>
        <div class="card flex-1">
          <RevenueTrendChart
            v-if="peak"
            :values="trendValues"
            :peakIdx="peak.idx"
            :peakLabel="`${peak.label} · ${formatCurrency(peak.value)}`"/>
          <div v-if="peak" class="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
            היום החזק בתקופה: <b class="text-primary-700">{{ peak.label }} · {{ formatCurrency(peak.value) }}</b>
            <template v-if="peak.sharePct >= 25"> — {{ peak.sharePct }}% מכל ההכנסה</template>
          </div>
          <div v-else class="text-xs text-gray-400 py-6 text-center">אין נתוני הכנסות בתקופה</div>
        </div>
      </section>

      <!-- What earns the most -->
      <section class="flex flex-col">
        <h3 class="section-title">מה מכניס הכי הרבה</h3>
        <div class="card flex-1 !py-1">
          <template v-if="topServices.length">
            <AnalyticsRankRow
              v-for="(s, i) of topServices"
              :key="s.serviceId"
              :name="s.name"
              :value="formatCurrency(s.revenue)"
              :sub="`${s.count} תורים`"
              :pct="Math.round((s.revenue / topServices[0].revenue) * 100)"
              :last="i === topServices.length - 1"/>
            <div class="text-[10px] text-gray-400 pt-1 pb-2">
              הערכה לפי מחירי המחירון של התורים שבוצעו — לא לפי התשלומים בפועל
            </div>
          </template>
          <div v-else class="text-xs text-gray-400 py-6 text-center">אין תורים בתקופה שנבחרה</div>
        </div>
      </section>

      <!-- When the business is busy -->
      <section class="flex flex-col">
        <h3 class="section-title">מתי העסק עמוס</h3>
        <div class="card flex-1">
          <div class="flex gap-2 mb-3">
            <div class="flex-1 bg-primary-50 rounded-xl px-3 py-2">
              <div class="text-[10px] text-gray-500">היום העמוס</div>
              <div class="text-sm font-bold mt-0.5">{{ busiestDay ?? '—' }}</div>
            </div>
            <div class="flex-1 bg-primary-50 rounded-xl px-3 py-2">
              <div class="text-[10px] text-gray-500">השעות החזקות</div>
              <div class="text-sm font-bold mt-0.5" dir="ltr" style="text-align: right">{{ busiestSlot ?? '—' }}</div>
            </div>
          </div>
          <BusinessHeatmap :heatmap="data.appointments.heatmap"/>
        </div>
      </section>

      <!-- Your customers -->
      <section class="flex flex-col">
        <h3 class="section-title">הלקוחות שלך</h3>
        <div class="card flex-1">
          <div class="flex items-center gap-2.5 mb-3">
            <div class="text-2xl font-bold">{{ data.customers.total }}</div>
            <div class="text-[11px] text-gray-500 leading-snug">לקוחות רשומים<br/>{{ data.customers.active }} פעילים בתקופה</div>
            <div class="mr-auto text-left">
              <div class="text-[15px] font-bold text-primary">{{ data.customers.repeatRate }}%</div>
              <div class="text-[10px] text-gray-500">שיעור חזרה</div>
            </div>
          </div>
          <!-- new vs returning split -->
          <div class="flex h-2.5 rounded-full overflow-hidden gap-0.5">
            <div :style="{flex: `${data.customers.newCount} 1 0%`, background: '#8864ee'}"/>
            <div :style="{flex: `${data.customers.returningCount} 1 0%`, background: '#c3b9f9'}"/>
            <div :style="{flex: `${inactiveCustomers} 1 0%`, background: '#e4e4e7'}"/>
          </div>
          <div class="flex gap-3.5 mt-2 text-[11px] text-gray-600">
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" style="background:#8864ee"/>{{ data.customers.newCount }} חדשים</span>
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" style="background:#c3b9f9"/>{{ data.customers.returningCount }} חוזרים</span>
            <span v-if="inactiveCustomers > 0" class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" style="background:#e4e4e7"/>{{ inactiveCustomers }} לא פעילים</span>
          </div>
        </div>
      </section>

      <!-- Top customers -->
      <section v-if="data.customers.topCustomers.length" class="flex flex-col">
        <h3 class="section-title">10 הלקוחות המובילים</h3>
        <div class="card flex-1 flex flex-col gap-3">
          <div v-for="(c, i) in data.customers.topCustomers" :key="c.customerId" class="flex items-center gap-2.5">
            <div class="w-4 text-center text-[12px] font-bold text-gray-400 shrink-0">{{ i + 1 }}</div>
            <div class="w-[34px] h-[34px] rounded-full shrink-0 flex items-center justify-center text-[12px] font-bold" style="background:#edeafd;color:#6831d0">
              {{ c.name.slice(0, 2) }}
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-semibold truncate">{{ c.name }}</div>
              <div class="text-[11px] text-gray-500">{{ c.visits }} ביקורים</div>
            </div>
            <div class="text-[13px] font-bold shrink-0">{{ formatCurrency(c.totalSpend) }}</div>
          </div>
        </div>
      </section>

      <!-- Loyalty club -->
      <section v-if="data.loyalty && data.loyalty.members > 0" class="flex flex-col">
        <h3 class="section-title">מועדון הנקודות</h3>
        <div class="card flex-1">
          <div class="flex items-center gap-2.5 mb-3">
            <div class="text-2xl font-bold">{{ data.loyalty.members }}</div>
            <div class="text-[11px] text-gray-500 leading-snug">
              חברי מועדון<br/>{{ data.loyalty.rewardsRedeemed }} הטבות מומשו בתקופה
            </div>
            <div class="mr-auto text-left">
              <div class="text-[15px] font-bold" style="color:#a16207">{{ data.loyalty.openPoints }}</div>
              <div class="text-[10px] text-gray-500">נקודות פתוחות</div>
            </div>
          </div>
          <!-- redeemed vs open -->
          <div class="flex h-2.5 rounded-full overflow-hidden gap-0.5">
            <div :style="{flex: `${data.loyalty.pointsRedeemed} 1 0%`, background: '#f59e0b'}"/>
            <div :style="{flex: `${Math.max(data.loyalty.openPoints, 0)} 1 0%`, background: '#fde68a'}"/>
          </div>
          <div class="flex gap-3.5 mt-2 text-[11px] text-gray-600">
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" style="background:#f59e0b"/>{{ data.loyalty.pointsRedeemed }} מומשו בתקופה</span>
            <span class="flex items-center gap-1"><span class="w-2 h-2 rounded-[3px]" style="background:#fde68a"/>{{ data.loyalty.openPoints }} ממתינות למימוש</span>
          </div>
          <div v-if="data.loyalty.topMember" class="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2.5">
            <div class="w-[34px] h-[34px] rounded-full shrink-0 flex items-center justify-center" style="background:#fef9c3;color:#a16207">
              <i class="pi pi-trophy text-sm"/>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13px] font-semibold truncate">{{ data.loyalty.topMember.name }} מוביל עם {{ data.loyalty.topMember.points }} נקודות</div>
              <div class="text-[11px] text-gray-500">נצברו {{ data.loyalty.pointsEarned }} נקודות בתקופה</div>
            </div>
          </div>
        </div>
      </section>

      <!-- Payments cross-link -->
      <section v-if="showPaymentsCard" class="flex flex-col">
        <div
          v-wave
          class="card flex-1 flex items-center gap-3 cursor-pointer"
          @click="emit('goPayments')">
          <div class="w-9 h-9 rounded-[11px] shrink-0 flex items-center justify-center" style="background:#dcfce7">
            <i class="pi pi-credit-card" style="color:#15803d"/>
          </div>
          <div class="flex-1">
            <div class="text-[13px] font-bold">כספים וסליקה</div>
            <div class="text-[11px] text-gray-500 mt-0.5">
              {{ formatCurrency(data.revenue.netRevenue) }} נכנסו · {{ data.revenue.transactionsCount }} עסקאות בתקופה
            </div>
          </div>
          <i class="pi pi-chevron-left text-gray-400 text-sm"/>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {DateTime} from 'luxon';
import type {IAnalyticsDashboard} from '@torim-app/types';

import AnalyticsAiBlock from './AnalyticsAiBlock.vue';
import AnalyticsRankRow from './AnalyticsRankRow.vue';
import BusinessHeatmap from './BusinessHeatmap.vue';
import RevenueMixBar, {IMixSegment} from './RevenueMixBar.vue';
import RevenueTrendChart from './RevenueTrendChart.vue';
import {formatCurrency} from './analytics-charts';

const props = defineProps<{
  data: IAnalyticsDashboard;
  periodLabel: string;
  showPaymentsCard: boolean;
}>();

const emit = defineEmits<{
  ask: [question: string];
  questions: [questions: string[]];
  goPayments: [];
}>();

const WEEKDAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

const MIX_COLORS: Record<string, string> = {
  appointments: '#8864ee',
  bundles: '#f59e0b',
  vouchers: '#ec4899',
  products: '#14b8a6',
  advanced: '#3b82f6',
  other: '#64748b',
};

// ─── Hero ────────────────────────────────────────────────────────────────

// All categories are kept (even at ₪0) so the legend is stable; the bar itself
// only draws segments with revenue.
const mixSegments = computed<IMixSegment[]>(() =>
  props.data.revenue.revenueByReason.map(r => ({
    key: r.key,
    label: r.label,
    value: r.value,
    color: MIX_COLORS[r.key] ?? '#64748b',
    count: r.count,
    countHint: 'תשלומים',
  })));

const hasMixRevenue = computed(() => mixSegments.value.some(s => s.value > 0));

// Tapping a mix segment swaps the hero amount to that category's total
const selectedMix = ref<IMixSegment | null>(null);
watch(() => props.data, () => selectedMix.value = null);

const revenueDelta = computed(() => {
  const pct = props.data.overview.revenue.changePct;
  if (pct === null || pct === 0) return null;
  const up = pct > 0;
  return {up, text: `${up ? '↑' : '↓'} ${up ? '+' : ''}${Math.round(pct)}% מהתקופה הקודמת`};
});

// ─── Pulse ───────────────────────────────────────────────────────────────

const escapeHtml = (s: string | number) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const pulseRows = computed(() => {
  const a = props.data.appointments;
  const c = props.data.customers;
  const o = props.data.overview;
  return [
    {
      icon: 'pi-calendar',
      tint: '#dbeafe',
      fg: '#1d4ed8',
      html: `<b>${escapeHtml(a.totalBooked)} תורים</b> נקבעו · עסקה ממוצעת <b>${escapeHtml(formatCurrency(o.avgTransactionValue))}</b>`,
    },
    {
      icon: 'pi-users',
      tint: '#dcfce7',
      fg: '#15803d',
      html: `<b>${escapeHtml(c.newCount)} לקוחות חדשים</b> הצטרפו · ${escapeHtml(c.returningCount)} חזרו שוב`,
    },
    {
      icon: 'pi-times-circle',
      tint: '#fee2e2',
      fg: '#b91c1c',
      html: `<b>${escapeHtml(a.cancelled)} ביטולים</b> (${escapeHtml(a.cancelledRate)}%) · ${escapeHtml(a.noShow)} אי-הגעות`,
    },
  ];
});

// ─── Trend ───────────────────────────────────────────────────────────────

const peak = computed(() => {
  const trend = props.data.revenue.revenueTrend;
  if (!trend.length) return null;
  let idx = 0;
  for (let i = 1; i < trend.length; i++) if (trend[i].value > trend[idx].value) idx = i;
  if (trend[idx].value <= 0) return null;
  const total = props.data.revenue.netRevenue || 1;
  const dt = props.data.revenue.trendGranularity === 'daily'
    ? DateTime.fromFormat(trend[idx].date, 'yyyy-MM-dd')
    : DateTime.fromFormat(trend[idx].date, 'yyyy-MM');
  const label = dt.isValid
    ? (props.data.revenue.trendGranularity === 'daily' ? dt.setLocale('he').toFormat('d בMMMM') : dt.setLocale('he').toFormat('MMMM yyyy'))
    : trend[idx].date;
  return {
    idx,
    value: trend[idx].value,
    label,
    sharePct: Math.round((trend[idx].value / total) * 100),
  };
});

const trendValues = computed(() => props.data.revenue.revenueTrend.map(p => p.value));

// ─── Services / busy / customers ─────────────────────────────────────────

const topServices = computed(() => props.data.overview.topServices.slice(0, 6));

const busiestDay = computed(() => {
  const byWeekday = props.data.appointments.byWeekday;
  const max = Math.max(...byWeekday);
  if (max <= 0) return null;
  return WEEKDAYS[byWeekday.indexOf(max)];
});

const busiestSlot = computed(() => {
  const cells = props.data.appointments.heatmap;
  if (!cells.length) return null;
  const top = [...cells].sort((a, b) => b.value - a.value)[0];
  if (!top || top.value <= 0) return null;
  return `${top.hour}:00–${top.hour + 2}:00`;
});

const inactiveCustomers = computed(() =>
  Math.max(props.data.customers.total - props.data.customers.newCount - props.data.customers.returningCount, 0));
</script>

<style scoped lang="scss">
.section-title {
  font-size: 13.5px;
  font-weight: 700;
  color: rgb(24 24 27);
  margin: 0 3px 9px;
}

.card {
  background: #fff;
  border: 1px solid rgb(228 228 231);
  border-radius: 18px;
  padding: 14px 15px;
}
</style>
